import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { SettingsBackupRestore, VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";

import { useCheckpoints, useUserDetail, useUserPermissions } from "../../services/hooks";
import MainContent from "../Layout/MainContent";
import theme, { buttonClasses } from "../utils/theme";
import UploadFileButton, { AccordionWrapper, AddRemove, Button, Checkbox, ColorPicker, Dialog, Dropdown, Input, Loader, toastMessage } from "../utils";
import { flattenAttributes } from "../../services/functions";
import "./Settings.css";
import { useGetOrgSettingQuery, useGetTemplateDetailOnCallMutation, useUpdateOrgSettingMutation, useUpdatePlantThemeMutation } from "../../api/vmsSlice";
import { useGetOrgBrandingQuery, useUpdateOrgThemeMutation } from "../../api/userSlice";
import RuleManagement from "./RuleManagement";
import AlertConfig from "./AlertConfig";
import { usePlantContext } from "../../context/plantContext";
import { useBranding } from "../../context/brandingContext";
import { ASSET_ACCEPT, AssetFileError, LEAF_PATHS, buildThemePatch, canHideInScope, fileToUploadDescriptor, leafGet, leafHasOwn, resolveBranding, TITLE_MAX_LENGTH } from "../../services/branding";
import RouteValidation from "./RouteValidation";
import { useNavigate } from "react-router-dom";
import { confirmThemeLeave, registerLeaveGuard } from "../../services/navigation";

function getDefaultPrefix() {
	return { type: "", value: "" };
}
function getDefaultPrefixConfig() {
	return {
		isDefault: false,
		reset_type: "",
		prefix_type: "vehicleEntry",
		prefix_sub_type: "",
		prefix: [getDefaultPrefix()],
	};
}

const BASIC_DETAILS = {
	displayName: "Basic Details",
	checkpointId: "BasicDetails",
	fields: [
		{ label: "Vehicle No.", value: "", isChecked: false },
		{ label: "Supplier", value: "", isChecked: false },
		{ label: "Product Category", value: "", isChecked: false },
		{ label: "Product", value: "", isChecked: false },
		{ label: "Trip ID", value: "", isChecked: false },
		{ label: "Driver Name", value: "", isChecked: false },
		{ label: "Driver Number", value: "", isChecked: false },
		{ label: "Allocate RFID Tag ( Optional )", value: "", isChecked: false },
		{ label: "Vehicle RC Number", value: "", isChecked: false },
		{ label: "Trip Status", value: "", isChecked: false },
		{ label: "Remarks", value: "", isChecked: false },
		{ label: "Tag tId", value: "", isChecked: false },
		{ label: "Tag EPC", value: "", isChecked: false },
		{ label: "Tag Type", value: "", isChecked: false },
		{ label: "Tag Display Name", value: "", isChecked: false },
		{ label: "Vehicle In Time", value: "", isChecked: false },
		{ label: "Vehicle Out Time", value: "", isChecked: false },
	],
};

const DEFAULT_FORM = {
	prefixes: [getDefaultPrefixConfig()],

	pdfSettings: {
		doc_type: "vehicle_detail",
		default_fields: [BASIC_DETAILS],
	},
};

function useOrgSetting({plantId}) {
	const { data, isFetching } = useGetOrgSettingQuery({plantId});
	let orgSetting = useMemo(() => {
		let { data: orgSetting = {} } = data || { data: {} };
		return orgSetting;
	}, [data]);

	const [updateOrgSetting, { isLoading }] = useUpdateOrgSettingMutation();
	return { orgSetting, isOrgSettingLoading: isFetching, updateOrgSetting, isOrgSettingUpdating: isLoading };
}

function Settings() {
	const { userDetail } = useUserDetail();
	const { userPermissions } = useUserPermissions();
	const { checkpoints } = useCheckpoints();
	const [getTemplateDetail] = useGetTemplateDetailOnCallMutation();
	const { plantId, plantOptions, loading, updatePlantId, isCheckpointForm} = usePlantContext();
	const selectedDefaultPlant = plantOptions.find((opt) => opt.value === plantId) || plantOptions[0] || null;
	const [selectedPlant, setSelectedPlant] = useState(selectedDefaultPlant);
	const { orgSetting, isOrgSettingLoading, updateOrgSetting, isOrgSettingUpdating } = useOrgSetting({'plantId' : selectedPlant?.value});
	const navigate = useNavigate();
	const handleGoBack = () => {
		navigate(-1); 
	};

	useEffect(() => {
    if (!Object.keys(orgSetting).length) {
      setForm((o) => ({
        ...o,
        prefixes: [getDefaultPrefixConfig()],
      }));
      return;
    }
    setForm((o) => ({
      ...o,
      prefixes: orgSetting.prefixArray.map((_) => ({
        isDefault: _.isDefault,
        reset_type: _.reset_type,
        prefix_type: _.prefix_type,
        prefix_sub_type: _.prefix_sub_type,
        prefix: _.prefix,
      })),
    }));
  }, [orgSetting]);

	const showThemeSettings = userDetail?.userType === "superAdmin" || userPermissions?.manageTheme?.value;
	// const [tab, setTab] = useState(showThemeSettings ? "theme" : "prefix");
	const [tab, setTab] = useState(() => {
		if (showThemeSettings) return "theme";
		if (userPermissions.modifyTripPrefix?.value) return "prefix";
		if (userPermissions.customizeLabels?.value) return "pdf";
		if (userPermissions.manageRules?.value) return "rule";
		if (userPermissions.manageRouteValidations?.value) return "routeValidation";
		if (userPermissions.manageAlerts?.value) return "alert";
		return ""; // Default to empty if no permissions
	});

	const [form, setForm] = useState(DEFAULT_FORM);

	const handlePrefixChange = (e, prefixIndex, prefixPartIndex) => {
		let { name, value, type, checked } = e.target;
		value = type == "checkbox" ? checked : value;
		setForm((o) => ({
			...o,
			prefixes: o.prefixes.map((_, i) => {
				if (i == prefixIndex) {
					if (prefixPartIndex != undefined) {
						return {
							..._,
							prefix: _.prefix.map((_, j) => {
								if (j == prefixPartIndex) {
									return { ..._, value: name == "type" ? "" : _.value, [name]: value };
								}
								return _;
							}),
						};
					}
					return { ..._, [name]: value };
				}
				if (name == "isDefault") {
					return { ..._, isDefault: false };
				}
				return _;
			}),
		}));
	};
	const handleSettingSubmit = (type) => {
		let error = {};
		let anyIsDefaultTrue = true;
		if (type == "prefix") {
			// console.log(form);
			form.prefixes.forEach(({ prefix, isDefault }, i) => {
				if(isDefault){
					anyIsDefaultTrue = false
				}
				prefix.forEach((p) => {
					if (!p.type || !p.value) {
						if (!["counter"].includes(p.type)) {
							error[i] = "Missing Value";
						}
					}
				});
			});
			
		}
		if(anyIsDefaultTrue){
			return toastMessage(false, "Exactly one prefix must be marked as default");
		}
		if (Object.keys(error).length) {
			toastMessage(false, "Please fill in the input values before submitting");
		} else {
			const payload = plantId ? {"plantId" : selectedPlant.value , ...form} : form;
			// console.log('payload', payload)
			updateOrgSetting(payload)
				.unwrap()
				.then((res) => {
					if (res.error) {
						toastMessage(false, res.message);
					} else {
						toastMessage(true, res.message);
					}
				})
				.catch((err) => {
					// console.log(err);
					toastMessage(false, "Error in updating theme setting");
				});
		}
	};
	const handleReset = () => {
		// console.log('tab=>', tab)
		if(tab === 'prefix'){
			setForm({
				...form,
				prefixes: [{
					isDefault: true,
					reset_type: "",
					prefix_type: "vehicleEntry",
					prefix_sub_type: "",
					prefix: [{type: "global", value: "TR"}],
				}]
			});
		}
		if(tab === 'pdf'){
			setForm({
				...form,
				pdfSettings: { ...form.pdfSettings, default_fields: form.pdfSettings.default_fields.map((_) => ({ ..._, fields: _.fields.map((_) => ({ ..._, isChecked: false })) })) },
			});
		}
	};
	const prefixUI = (
    <>
      {isOrgSettingLoading || isOrgSettingUpdating ? (
        <Loader size="2rem" height="8.5rem" />
      ) : (
        <section className="prefixes">
          <div style={{ minWidth: "200px" }}>
              {plantOptions && plantOptions.length > 1 ? <Dropdown
                label="Select Warehouse"
                options={plantOptions || []}
				value={selectedPlant}
                onChange={(value)=> setSelectedPlant(value) }
                // disabled={loading || !plantOptions || plantOptions.length === 0}
                // disableClearable={true}
              /> : null}
          </div>
          {form.prefixes.map(({ reset_type, prefix, isDefault }, i) => {
            let prefixResult = prefix.reduce((res, curr) => {
              switch (curr.type) {
                case "date":
                case "global":
                  res += curr.value ? `<${curr.value}>` : "";
                  break;
                case "finance_year":
                  res += curr.value ? `<${financialPrefixOptions.find((_) => _.value == curr.value)?.label}>` : "";
                  break;
                case "product":
                  res += curr.value ? `<product-${curr.value}>` : "";
                  break;
                case "productCategories":
                  res += curr.value ? `<productCategories-${curr.value}>` : "";
                  break;
                case "supplier":
                  res += curr.value ? `<supplier-${curr.value}>` : "";
                  break;
                case "counter":
                  res += "<COUNTER>";
                  break;
              }
              return res;
            }, "");
            if (!prefixResult.includes("<COUNTER>")) {
              prefixResult += "<COUNTER>";
            }

            return (
              <div className="prefix" key={i}>
                <div className="prefix-row">
                  <Checkbox label="Default Prefix" name="isDefault" checked={isDefault} onChange={(e) => handlePrefixChange(e, i)} />
                  <AddRemove
                    list={form.prefixes}
                    filterMethod={({ prefix }) => prefix.every(({ type, value }) => type == "" && value == "")}
                    onAdd={() => setForm((o) => ({ ...o, prefixes: [...o.prefixes, getDefaultPrefixConfig()] }))}
                    onRemove={(index) => setForm((o) => ({ ...o, prefixes: o.prefixes.filter((_, pI) => pI != index) }))}
                    index={i}
                    outerIndex={i}
                  />
                </div>

                {prefix.map(({ type, value }, j) => {
                  return (
                    <div key={j} className="form-inputs" style={{ marginTop: "8px" }}>
                      <Dropdown
                        label="Type"
                        name="type"
                        value={prefixTypeOptions.find((_) => _.value == type) || null}
                        options={prefixTypeOptions}
                        onChange={(opt) => {
                          handlePrefixChange({ target: { name: "type", value: opt?.value || "" } }, i, j);
                        }}
                        getOptionDisabled={(option) => {
                          return !!prefix.find((_) => _.type == option.value);
                        }}
                      />
                      <div className="prefix-input-wrap">
                        {type == "date" ? (
                          <>
                            <Dropdown
                              label="Format"
                              name="value"
                              value={datePrefixOptions.find((_) => _.value == value) || null}
                              options={datePrefixOptions}
                              onChange={(opt) => {
                                handlePrefixChange({ target: { name: "value", value: opt?.value || "" } }, i, j);
                              }}
                            />
                            <Dropdown
                              label="Reset On"
                              name="reset_type"
                              value={resetOptions.find((_) => _.value == reset_type) || null}
                              options={resetOptions}
                              onChange={(opt) => {
                                handlePrefixChange({ target: { name: "reset_type", value: opt?.value || "" } }, i);
                              }}
                            />
                          </>
                        ) : type == "finance_year" ? (
                          <Dropdown
                            label="Format"
                            name="value"
                            value={financialPrefixOptions.find((_) => _.value == value) || null}
                            options={financialPrefixOptions}
                            onChange={(opt) => {
                              handlePrefixChange({ target: { name: "value", value: opt?.value || "" } }, i, j);
                            }}
                          />
                        ) : type == "global" ? (
                          <Input label="Value" name="value" value={value} onChange={(e) => handlePrefixChange(e, i, j)} />
                        ) : ["product"].includes(type) ? (
                          <Dropdown
                            label="Format"
                            name="value"
                            value={productPrefixOptions.find((_) => _.value == value) || null}
                            options={productPrefixOptions}
                            onChange={(opt) => {
                              handlePrefixChange({ target: { name: "value", value: opt?.value || "" } }, i, j);
                            }}
                          />
                        ) : ["productCategories"].includes(type) ? (
                          <Dropdown
                            label="Format"
                            name="value"
                            value={productCatPrefixOptions.find((_) => _.value == value) || null}
                            options={productCatPrefixOptions}
                            onChange={(opt) => {
                              handlePrefixChange({ target: { name: "value", value: opt?.value || "" } }, i, j);
                            }}
                          />
                        ) : ["supplier"].includes(type) ? (
                          <Dropdown
                            label="Format"
                            name="value"
                            value={supplierPrefixOptions.find((_) => _.value == value) || null}
                            options={supplierPrefixOptions}
                            onChange={(opt) => {
                              handlePrefixChange({ target: { name: "value", value: opt?.value || "" } }, i, j);
                            }}
                          />
                        ) : null}
                      </div>

                      <AddRemove
                        list={prefix}
                        filterMethod={({ type, value }) => type == "" && value == ""}
                        onAdd={() => {
                          setForm((o) => ({
                            ...o,
                            prefixes: o.prefixes.map((_, pI) => {
                              if (pI == i) {
                                return { ..._, prefix: [..._.prefix, getDefaultPrefix()] };
                              }
                              return _;
                            }),
                          }));
                        }}
                        onRemove={(index) => {
                          setForm((o) => ({
                            ...o,
                            prefixes: o.prefixes.map((_, pI) => {
                              if (pI == i) {
                                return { ..._, prefix: _.prefix.filter((_, j) => j != index) };
                              }
                              return _;
                            }),
                          }));
                        }}
                        index={j}
                        outerIndex={j}
                      />
                    </div>
                  );
                })}

                <div className="prefix_output">
                  <div>RESULT:</div>
                  <div>
                    <b>{prefixResult}</b>
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      )}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem", marginTop: "5rem" }}>
        <Button onClick={() => handleReset()} text="Reset" style={{ ...buttonClasses.lynkitBlackEmpty, width: "fit-content" }} disabled={isOrgSettingLoading || isOrgSettingUpdating} />
        <Button
          onClick={() => handleSettingSubmit("prefix")}
          text="Save Changes"
          style={{ ...buttonClasses.lynkitOrangeEmpty, width: "fit-content" }}
          disabled={isOrgSettingLoading || isOrgSettingUpdating}
        />
      </div>
    </>
  );

	useEffect(() => {
		if (tab !== "pdf") return;
		if (!checkpoints.length) return;
		if (form.pdfSettings.doc_type == "vehicle_detail") {
			let dataMap = new Map(); // used becasue it maintains insertion order
			let alreadyFetchedPromise = {};
			for (let c of checkpoints) {
				let templateId = c.templateId?.trim();
				let checkoutTemplateId = c.checkoutConfig?.templateId?.trim();

				if (templateId && !alreadyFetchedPromise[templateId]) {
					alreadyFetchedPromise[templateId] = getTemplateDetail({ id: templateId }).unwrap();
				}
				if (checkoutTemplateId && !alreadyFetchedPromise[checkoutTemplateId]) {
					alreadyFetchedPromise[checkoutTemplateId] = getTemplateDetail({ id: checkoutTemplateId }).unwrap();
				}

				dataMap.set(c._id, {
					displayName: c.displayName,
					checkpointNumber: c.checkpointNumber,
					templatePromise: alreadyFetchedPromise[templateId],
					chkoutTemplatePromise: alreadyFetchedPromise[checkoutTemplateId],
				});
			}

			let finalData = [];

			async function processMap(dataMap) {
				for (let [chkptId, info] of dataMap) {
					let res;
					let resObj = {};
					resObj.checkpointId = chkptId;
					resObj.displayName = info?.displayName;
					resObj.checkpointNumber = info?.checkpointNumber;
					const existingData = orgSetting?.pdfSettings?.default_fields?.find((_) => _.checkpointId == chkptId);
					if (info.templatePromise) {
						res = await info.templatePromise;
						if (!res.error) {
							resObj.fields = flattenAttributes(res.data.attributes);

							let _res;
							if (info.chkoutTemplatePromise) {
								_res = await info.chkoutTemplatePromise;
								if (!_res.error) {
									resObj.fields.push(...flattenAttributes(_res.data.attributes));
								}
							} else {
								_res = true; // some hack
							}

							if (_res) {
								resObj.fields = resObj.fields
									.filter((_) => {
										return !["fetch", "table", "submit"].includes(_.type);
									})
									.map((_) => {
										let existField = existingData?.fields?.find((f) => f.label == _._id);
										return { isChecked: !!existField?.isChecked, label: _._id, value: "" };
									});
							}
						}
					} else if (info.chkoutTemplatePromise) {
						res = await info.chkoutTemplatePromise;
						if (!res.error) {
							resObj.fields = flattenAttributes(res.data.attributes);
						}
					}
					finalData.push(resObj);
				}
				// console.log(finalData);
				setForm((o) => ({ ...o, pdfSettings: { ...o.pdfSettings, default_fields: [orgSetting?.pdfSettings?.default_fields[0] || BASIC_DETAILS, ...finalData] } }));
			}
			processMap(dataMap);
		}
	}, [tab, form.pdfSettings.doc_type, orgSetting, checkpoints]);

	const pdfUI = (
		<>
			{isOrgSettingLoading || isOrgSettingUpdating ? (
				<Loader size="2rem" height="8.5rem" />
			) : (
				<div className="print_selection_container">
					<Dropdown
						label="Document Type"
						name="pdfSettings.doc_type"
						value={pdfDocTypeOptions.find((_) => _.value == form.pdfSettings.doc_type) || null}
						options={pdfDocTypeOptions}
						readonly
						disabled
					/>
					{form.pdfSettings?.default_fields?.map((ele, i) => {
						return (
							<div key={i} style={{ marginTop: i == 0 ? "1rem" : "" }}>
								<AccordionWrapper
									title={
										<div className="level_heading">
											<Checkbox
												id={ele.displayName}
												name={ele.displayName}
												checked={ele?.fields?.every((ele) => ele.isChecked) ? true : false}
												onChange={(e) => {
													e.stopPropagation();
													setForm((o) => ({
														...o,
														pdfSettings: {
															...o.pdfSettings,
															default_fields: o.pdfSettings.default_fields.map((_, j) => {
																if (i == j) {
																	return {
																		..._,
																		fields: _.fields.map((_) => {
																			return { ..._, isChecked: e.target.checked };
																		}),
																	};
																}
																return _;
															}),
														},
													}));
												}}
											/>
											<Input
												value={ele.displayName}
												onChange={(e) => {
													setForm((o) => ({
														...o,
														pdfSettings: {
															...o.pdfSettings,
															default_fields: o.pdfSettings.default_fields.map((_, j) => {
																if (i == j) {
																	return { ..._, displayName: e.target.value };
																}
																return _;
															}),
														},
													}));
												}}
												sx={{ width: "30rem" }}
											/>
										</div>
									}
								>
									<div className="level_1">
										{ele?.fields?.map((el, ind) => (
											<div style={{ width: "260px" }} key={el.label + ind}>
												<Checkbox
													id={el.label}
													name={el.label}
													checked={el.isChecked}
													label={el.label}
													onChange={(e) => {
														setForm((o) => ({
															...o,
															pdfSettings: {
																...o.pdfSettings,
																default_fields: o.pdfSettings.default_fields.map((_) => {
																	if (_.displayName === ele.displayName) {
																		return {
																			..._,
																			fields: _.fields.map((_) => {
																				if (_.label === e.target.name) {
																					return { ..._, isChecked: e.target.checked };
																				}
																				return _;
																			}),
																		};
																	}
																	return _;
																}),
															},
														}));
													}}
												/>
											</div>
										))}
									</div>
								</AccordionWrapper>
							</div>
						);
					})}
				</div>
			)}
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem", marginTop: "3rem" }}>
				<Button onClick={handleReset} text="Reset" style={{ ...buttonClasses.lynkitBlackEmpty, width: "fit-content" }} disabled={isOrgSettingLoading || isOrgSettingUpdating} />
				<Button onClick={handleSettingSubmit} text="Save Changes" style={{ ...buttonClasses.lynkitOrangeEmpty, width: "fit-content" }} disabled={isOrgSettingLoading || isOrgSettingUpdating} />
			</div>
		</>
	);

	const ruleUI=(<>
	{isOrgSettingLoading || isOrgSettingUpdating ? (
				<Loader size="2rem" height="8.5rem" />
			):(<>
				 <RuleManagement/> 
			</>)}
		
	</>)
	const routeValidationUI=(<>
	{isOrgSettingLoading || isOrgSettingUpdating ? (
				<Loader size="2rem" height="8.5rem" />
			):(<>
				 <RouteValidation/> 
			</>)}
		
	</>)
	const alertUI = (<>{isOrgSettingLoading || isOrgSettingUpdating ? (<Loader size="2rem" height="8.5rem" />) : <><AlertConfig/></>}</>)


	let tabContentUI;
	switch (tab) {
		case "theme":
			tabContentUI = <ThemeSetting showThemeSettings={showThemeSettings} />;
			break;
		case "prefix":
			tabContentUI = prefixUI;
			break;
		case "pdf":
			tabContentUI = pdfUI;
			break;
		case "rule":
			tabContentUI= ruleUI;
			break;
		case "routeValidation":
			tabContentUI= routeValidationUI;
			break;
		case "alert":
			tabContentUI= alertUI;
			break
		default:
			tabContentUI = <></>;
	}

	return (
		<MainContent title="Settings" panel="Go Back" btnFunc={handleGoBack}>
			<Box>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={tab}
						onChange={(e, v) => { if (v === tab || confirmThemeLeave()) setTab(v); }}
						aria-label="setting tabs"
						sx={{
							"& .MuiTabs-indicator": { backgroundColor: "var(--primary-color)" },
							"& .MuiTab-root": { color: "var(--text-color)" },
							"& .MuiTab-root.Mui-selected": { color: "var(--primary-color)" },
						}}
					>
						{showThemeSettings ? <Tab label="Theme Options" value="theme" {...a11yProps(0)} /> : null}

						{userPermissions.modifyTripPrefix?.value && <Tab label="Prefix Options" value="prefix" {...a11yProps(1)} />}		
						{userPermissions.modifyTripPrefix?.value && <Tab label="PDF Options" value="pdf" {...a11yProps(2)} />}
						{userPermissions.manageRules?.value && <Tab label="Rule Management" value="rule" {...a11yProps(3)} />}
						{userPermissions.manageRouteValidations?.value && <Tab label= "Route Validation" value= "routeValidation" {...a11yProps(4)}/>}
						{userPermissions.manageAlerts?.value && <Tab label="Alert Configuration" value="alert" {...a11yProps(5)} />}
						
					</Tabs>
				</Box>
				{tabContentUI}
			</Box>
		</MainContent>
	);
}

export default Settings;

// In a plant-scoped editor the only distinction worth a chip is "changed here": an unsaved edit, a
// saved plant override, or an asset hidden for this plant. Everything else is inherited, which the
// scope header states once for the whole page rather than repeating on all eight cards. Whether a
// given inherited value came from the organization or the application default stays available as
// the field label's tooltip.
//
// The same editor also edits the organization layer itself ("org" scope). There the layer being
// edited is fed to the resolver in the plant slot with an empty parent, so `source: "plant"` reads
// "set for organization" and everything else is the application default. Only wording and the
// hide action differ between scopes; all of it lives here so the editor body stays shared.
const SCOPE_COPY = {
	plant: {
		ownSource: "Customized for this warehouse",
		ownBadge: "Customized",
		inheritedBadge: null,
		revertLabel: "Use organization default",
		revertTooltip: (label) => `Use the organization default for ${label}`,
		revertNoop: (label) => `${label} already uses the organization default`,
		resetAll: "Reset all to organization defaults",
		appearanceSub: "Colour scheme for this warehouse",
		stale: "This warehouse's theme changed since it was opened here.",
		loadError: "Could not load the theme. Editing is disabled until both warehouse and organization settings load. Please refresh and try again.",
		reloadError: "Could not reload this warehouse's theme. Please try again.",
		previewNote: "Sample screen — updates as you edit. Save to apply it for everyone at this warehouse.",
	},
	org: {
		ownSource: "Set for organization",
		ownBadge: "Set for organization",
		inheritedBadge: "App default",
		revertLabel: "Use app default",
		revertTooltip: (label) => `Use the app default for ${label}`,
		revertNoop: (label) => `${label} already uses the app default`,
		resetAll: "Reset all to app defaults",
		appearanceSub: "Colour scheme for the organization",
		stale: "The organization theme changed since it was opened here.",
		loadError: "Could not load the organization theme. Please refresh and try again.",
		reloadError: "Could not reload the organization theme. Please try again.",
		previewNote: "Sample screen — updates as you edit. Save to apply it to every warehouse that hasn't overridden a field.",
	},
};
const ThemeScopeContext = createContext("plant");
function useScopeCopy() {
	return SCOPE_COPY[useContext(ThemeScopeContext)] || SCOPE_COPY.plant;
}

function sourceLabel(source, scope = "plant") {
	if (source === "plant") return SCOPE_COPY[scope].ownSource;
	if (source === "organization") return "Inherited from organization";
	return "Application default";
}

// In a plant-scoped editor the only distinction worth a chip is "changed here": an unsaved edit, a
// saved plant override, or an asset hidden for this plant. Everything else is inherited, which the
// scope header states once for the whole page. "Hidden" describes the draft and stacks with
// "Unsaved", which would otherwise mask it the moment you hide something; "Customized" means a
// SAVED override, so a pending edit shows "Unsaved" in its place instead.
function SourceBadge({ resolved, dirty }) {
	const copy = useScopeCopy();
	return (
		<span className="theme-badges">
			{resolved.hidden ? <span className="theme-badge theme-badge-hidden">Hidden</span> : null}
			{dirty ? (
				<span className="theme-badge theme-badge-dirty">Unsaved</span>
			) : resolved.source === "plant" ? (
				<span className="theme-badge theme-badge-plant">{copy.ownBadge}</span>
			) : copy.inheritedBadge ? (
				<span className="theme-badge theme-badge-default">{copy.inheritedBadge}</span>
			) : null}
		</span>
	);
}

// Icon-only control with a hover tooltip. The tooltip is the explanation, so it carries the
// state-specific wording ("already using…", "show again") rather than one static label; the
// `aria-label` names the action for screen readers. MUI suppresses tooltips on a disabled button,
// hence the wrapping span.
function FieldTool({ tooltip, label, onClick, disabled, children }) {
	return (
		<Tooltip title={tooltip} arrow>
			<span className="theme-tool-wrap">
				<IconButton className="theme-tool" size="small" aria-label={label} onClick={onClick} disabled={disabled}>
					{children}
				</IconButton>
			</span>
		</Tooltip>
	);
}

// The revert tool is disabled rather than removed when a field has nothing to revert, so every row
// keeps the same controls in the same column and nothing shifts as you edit. `onToggleHide` is
// omitted for scalar fields, which cannot be hidden — only assets get the eye.
function FieldTools({ label, resolved, canRevert, onRevert, onToggleHide, disabled }) {
	const copy = useScopeCopy();
	return (
		<div className="theme-row-tools">
			<FieldTool
				tooltip={canRevert ? copy.revertTooltip(label) : copy.revertNoop(label)}
				label={copy.revertLabel}
				onClick={onRevert}
				disabled={disabled || !canRevert}
			>
				<SettingsBackupRestore fontSize="small" />
			</FieldTool>
			{onToggleHide ? (
				<FieldTool
					tooltip={resolved.hidden ? `Show ${label} again` : `Hide ${label} for this warehouse`}
					label={resolved.hidden ? "Show again" : "Hide for this warehouse"}
					onClick={onToggleHide}
					disabled={disabled}
				>
					{resolved.hidden ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
				</FieldTool>
			) : null}
		</div>
	);
}

function SettingsPanel({ title, subtitle, children }) {
	return (
		<section className="theme-panel">
			<header className="theme-panel-head">
				<h3 className="theme-panel-title">{title}</h3>
				<span className="theme-panel-sub">{subtitle}</span>
			</header>
			{children}
		</section>
	);
}

// One row on a three-column grid — label rail, control, tool rail — shared by every field so the
// columns line up down the whole panel. `hint` is the format/placement guidance that had nowhere
// to live in the old card layout.
function FieldRow({ label, hint, resolved, dirty, disabled, canRevert, onRevert, onToggleHide, children }) {
	const scope = useContext(ThemeScopeContext);
	return (
		<div className={`theme-row${dirty ? " is-dirty" : ""}`}>
			<div className="theme-row-label">
				<div className="theme-row-name">
					<span className="theme-row-title" title={sourceLabel(resolved.source, scope)}>{label}</span>
					<SourceBadge resolved={resolved} dirty={dirty} />
				</div>
				{hint ? <span className="theme-row-hint">{hint}</span> : null}
			</div>
			<div className="theme-row-control">{children}</div>
			<FieldTools label={label} resolved={resolved} canRevert={canRevert} onRevert={onRevert} onToggleHide={onToggleHide} disabled={disabled} />
		</div>
	);
}

function assetFileName(resolved) {
	if (resolved.hidden) return "Hidden for this warehouse";
	if (resolved.value?.fileName) return resolved.value.fileName;
	if (resolved.value?.url) return decodeURIComponent(resolved.value.url.split("/").pop().split("?")[0]);
	return "";
}

function AssetRow({ label, hint, path, resolved, overridden, hasPendingOp, allowedExtensions, onUpload, onCancelEdit, onHide, onUnhide, onUseOrgDefault, disabled }) {
	const scope = useContext(ThemeScopeContext);
	const shown = !resolved.hidden && resolved.value?.url;
	return (
		<FieldRow
			label={label}
			hint={hint}
			resolved={resolved}
			dirty={hasPendingOp}
			disabled={disabled}
			canRevert={overridden || hasPendingOp}
			onRevert={onUseOrgDefault}
			onToggleHide={canHideInScope(scope) ? (resolved.hidden ? onUnhide : onHide) : undefined}
		>
			<div className="theme-asset">
				{shown ? <img className="theme-asset-thumb" src={resolved.value.url} alt="" /> : <span className="theme-asset-thumb theme-asset-thumb-empty" />}
				<span className="theme-asset-name">{assetFileName(resolved) || sourceLabel(resolved.source, scope)}</span>
				<UploadFileButton
					allowedExtensions={allowedExtensions}
					name={path}
					// Only a pending upload gets the filename, which is what puts the cancel "X" on the
					// button; the saved name is rendered beside it instead so the button stays compact.
					fileName={hasPendingOp ? assetFileName(resolved) : ""}
					clearable={hasPendingOp}
					onChange={onUpload}
					onChangeClear={onCancelEdit}
					id={`theme-asset-${path}`}
					buttonText={shown ? "Replace" : "Upload"}
					ariaLabel={`Upload ${label}`}
					width="auto"
					disabled={disabled}
				/>
			</div>
		</FieldRow>
	);
}

// Labels caption each tile so you can tell which dashboard tile an icon belongs to.
const STAT_TILES = [
	{ key: "total", label: "Total Icon" },
	{ key: "todayIn", label: "Today In Icon" },
	{ key: "todayOut", label: "Today Out Icon" },
	{ key: "currentIn", label: "Current In Icon" },
];

function StatIcon({ asset, color }) {
	if (!asset.hidden && asset.value?.url) return <img className="theme-tile-img" src={asset.value.url} alt="" />;
	return (
		<svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M3 13h2l2-5h9l2 5h2v5h-2a2 2 0 1 1-4 0H9a2 2 0 1 1-4 0H3z" />
		</svg>
	);
}

// One tile per dashboard icon: the icon at a size you can actually judge, captioned with its tile.
function StatTile({ tile, color, path, resolved, overridden, hasPendingOp, onUpload, onCancelEdit, onHide, onUnhide, onUseOrgDefault, disabled }) {
	const scope = useContext(ThemeScopeContext);
	const label = tile.label;
	return (
		<div className={`theme-tile${hasPendingOp ? " is-dirty" : ""}`}>
			<div className="theme-tile-head">
				<FieldTools
					label={label}
					resolved={resolved}
					canRevert={overridden || hasPendingOp}
					onRevert={onUseOrgDefault}
					onToggleHide={canHideInScope(scope) ? (resolved.hidden ? onUnhide : onHide) : undefined}
					disabled={disabled}
				/>
			</div>
			<span className="theme-tile-icon">{resolved.hidden ? null : <StatIcon asset={resolved} color={color} />}</span>
			<span className="theme-tile-caption">{tile.label}</span>
			<div className="theme-tile-foot">
				<UploadFileButton
					allowedExtensions={ASSET_ACCEPT}
					name={path}
					fileName={hasPendingOp ? assetFileName(resolved) : ""}
					clearable={hasPendingOp}
					onChange={onUpload}
					onChangeClear={onCancelEdit}
					id={`theme-asset-${path}`}
					buttonText={!resolved.hidden && resolved.value?.url ? "Replace icon" : "Upload icon"}
					ariaLabel={`Upload ${label}`}
					width="auto"
					disabled={disabled}
				/>
				<SourceBadge resolved={resolved} dirty={hasPendingOp} />
			</div>
		</div>
	);
}

// Relative luminance per WCAG 2.x, against white — the colour is used behind white button text.
function contrastWithWhite(hex) {
	const match = /^#([0-9a-f]{6})$/i.exec((hex || "").trim());
	if (!match) return null;
	const channels = [0, 2, 4]
		.map((i) => parseInt(match[1].slice(i, i + 2), 16) / 255)
		.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
	const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
	return 1.05 / (luminance + 0.05);
}

function ContrastNote({ color }) {
	const ratio = contrastWithWhite(color);
	if (ratio === null) return null;
	const passes = ratio >= 4.5;
	return (
		// Plain-language message; the technical contrast ratio stays available as a tooltip.
		<span className={`theme-contrast${passes ? " is-pass" : " is-warn"}`} title={`Contrast ${ratio.toFixed(1)}:1 (4.5:1 or higher recommended)`}>
			{passes ? "White text is readable on this colour" : "White text may be hard to read on this colour. Try a darker shade."}
		</span>
	);
}

// Colours are scoped to this box, never the global theme variables, so previewing dark mode cannot
// repaint the page the admin is working in. The dashboard tiles are deliberately absent: they are
// edited directly in the Dashboard tiles panel, and drawing them twice on one screen helps nobody.
function ThemePreview({ preview }) {
	const primary = preview.theme.primaryColor.value || "#ff7200";
	const isDark = preview.theme.selectedTheme.value === "darkMode";
	const title = preview.title.value || "LynkID";
	const logo = !preview.logo.hidden ? preview.logo.value?.url : null;
	const favIcon = !preview.favIcon.hidden ? preview.favIcon.value?.url : null;
	const palette = isDark
		? { "--tp-page": "#15161a", "--tp-surface": "#1e2026", "--tp-line": "#2f323a", "--tp-text": "#f1f2f4", "--tp-muted": "#9b9ea6" }
		: { "--tp-page": "#f4f5f7", "--tp-surface": "#ffffff", "--tp-line": "#e5e6ea", "--tp-text": "#16181d", "--tp-muted": "#6d7078" };
	return (
		<div className="theme-preview" style={{ ...palette, "--tp-primary": primary }} data-testid="theme-preview">
			<div className="tp-tabbar">
				<div className="tp-tab">
					{favIcon ? <img src={favIcon} alt="" className="tp-favicon" /> : <span className="tp-favicon tp-favicon-blank" />}
					<span className="tp-tab-title">{title}</span>
				</div>
			</div>
			<div className="tp-window">
				<div className="tp-topbar">
					{logo ? <img src={logo} alt="" className="tp-logo" /> : <span className="tp-logo-text">{title}</span>}
					<span className="tp-avatar" />
				</div>
				<div className="tp-body">
					<span className="tp-heading" />
					<span className="tp-line" />
					<span className="tp-line tp-line-short" />
					<div className="tp-actions">
						<span className="tp-btn">Create Trip</span>
						<span className="tp-link">View all</span>
					</div>
				</div>
			</div>
		</div>
	);
}

// Applies the editor's tracked pending operations on top of the plant's last-fetched raw
// `overrides` to build a preview-only draft. Never sent to the server and never touches the
// shared query cache — `buildThemePatch` (services/branding.js) is what actually gets saved,
// built straight from `pendingOps`, not from a diff of this merged preview.
function applyPendingOpsToDraft(overrides, pendingOps) {
	const draft = JSON.parse(JSON.stringify(overrides || {}));
	const setDeep = (path, value) => {
		const parts = path.split(".");
		let node = draft;
		for (let i = 0; i < parts.length - 1; i++) {
			if (!node[parts[i]] || typeof node[parts[i]] !== "object") node[parts[i]] = {};
			node = node[parts[i]];
		}
		node[parts[parts.length - 1]] = value;
	};
	const unsetDeep = (path) => {
		const parts = path.split(".");
		let node = draft;
		for (let i = 0; i < parts.length - 1; i++) {
			if (!node[parts[i]]) return;
			node = node[parts[i]];
		}
		delete node[parts[parts.length - 1]];
	};
	for (const [path, entry] of Object.entries(pendingOps)) {
		if (!entry) continue;
		if (entry.op === "unset") unsetDeep(path);
		else if (entry.op === "hide") setDeep(path, null);
		// Keep the picked file's name: without it the displayed name falls back to the blob: URL's UUID.
		else setDeep(path, entry.previewUrl ? { url: entry.previewUrl, fileName: entry.value?.fileName } : entry.value);
	}
	return draft;
}

const THEME_SCOPES = [
	{ value: "org", label: "Organization defaults" },
	{ value: "plant", label: "Warehouse" },
];

function ThemeSetting({ showThemeSettings }) {
	const branding = useBranding();
	const { userDetail } = useUserDetail();
	const { userPermissions } = useUserPermissions();
	const [scope, setScope] = useState("plant");
	if (!showThemeSettings) return null;
	const isSuperAdmin = userDetail?.userType === "superAdmin";
	// superAdmin has no home org: edit the org that owns the selected warehouse, as brandingContext does.
	const orgId = isSuperAdmin ? branding.orgId : userDetail?.orgId;
	const canEdit = isSuperAdmin || !!userPermissions?.manageTheme?.value;
	// Switching scope unmounts the current editor, so unsaved edits go through the same leave guard
	// the tabs use (it also refuses while a save is in flight).
	function switchScope(next) {
		if (next === scope || !confirmThemeLeave()) return;
		setScope(next);
	}
	const scopeSwitch = (
		<div className="theme-segmented theme-scope-switch" role="group" aria-label="Theme scope">
			{THEME_SCOPES.map((option) => {
				const active = scope === option.value;
				return (
					<button key={option.value} type="button" className={active ? "is-active" : ""} aria-pressed={active} onClick={() => switchScope(option.value)}>
						{option.label}
					</button>
				);
			})}
		</div>
	);
	return <ThemeEditor scope={scope} orgId={orgId} canEdit={canEdit} scopeSwitch={scopeSwitch} guardsPlantSwitch={scope === "plant" || isSuperAdmin} />;
}

/**
 * Field-level theme editor for one layer: a warehouse's overrides (`scope="plant"`, inheriting the
 * organization defaults) or the organization defaults themselves (`scope="org"`, inheriting the
 * application defaults). Each scope only supplies its data source; the editor body is shared.
 * The body is keyed by identity + scope + org + plant, so switching any of them starts a fresh
 * editor and callbacks from the old one cannot affect it.
 */
// `onDirtyChange(isDirty)` lets a host page (e.g. Edit Company tabs) mark unsaved theme work.
// `guardsPlantSwitch`: whether changing the selected warehouse would discard this editor's draft
// (always for warehouse scope; for org scope only when the org follows the warehouse).
export function ThemeEditor({ scope = "plant", orgId, canEdit, scopeSwitch, onDirtyChange, guardsPlantSwitch = scope === "plant" }) {
	const shared = { canEdit, scopeSwitch, onDirtyChange, guardsPlantSwitch };
	return scope === "org" ? <OrgThemeEditor orgId={orgId} {...shared} /> : <PlantThemeEditor {...shared} />;
}

function PlantThemeEditor(props) {
	const source = usePlantThemeSource();
	return <ThemeEditorBody key={`${source.userId}:plant:${source.orgId || ""}:${source.targetId || ""}`} scope="plant" source={source} {...props} />;
}

function OrgThemeEditor({ orgId, ...props }) {
	const source = useOrgThemeSource(orgId);
	return <ThemeEditorBody key={`${source.userId}:org:${orgId || ""}:`} scope="org" source={source} {...props} />;
}

const EMPTY_LAYER = Object.freeze({});
// Outlined editor buttons keep their brand-coloured border when disabled; mute it instead.
// `&&` outranks the shared Button's own `&.Mui-disabled` rule (which only sets the text colour).
const MUTED_WHEN_DISABLED = { "&&.Mui-disabled": { borderColor: "var(--gray-color)", backgroundColor: "transparent", opacity: 0.6 } };

// Warehouse layer: own = the plant's overrides, parent = the organization defaults, both from
// brandingContext (which already guards against responses for a previous plant/org).
function usePlantThemeSource() {
	const { plantOptions, plantId, updatePlantId, loading: plantsLoading, plantLoadError } = usePlantContext();
	const { userId, orgId, orgDefaults, plantThemeQuery, plantOverrides, revision, isReady, isError } = useBranding();
	const [updatePlantTheme, { isLoading }] = useUpdatePlantThemeMutation();
	const selectedPlant = plantOptions.find((opt) => opt.value === plantId);
	const showPicker = plantOptions && (plantOptions.length > 1 || (plantOptions.length === 1 && !selectedPlant));
	return {
		userId, orgId, targetId: plantId, idKey: "plantId",
		ownLayer: plantOverrides, parentLayer: orgDefaults, revision, isReady, isError, isSaving: isLoading,
		save: (patch) => updatePlantTheme({ plantId, ...patch }),
		refetch: () => plantThemeQuery.refetch(),
		title: selectedPlant ? `Theme for ${selectedPlant.label}` : "Warehouse theme",
		subtitle: (customizedCount) =>
			selectedPlant && isReady && !isError
				? `Inherits your organization defaults · ${customizedCount} of ${LEAF_PATHS.length} fields customized here`
				: "Every field falls back to your organization defaults until you override it here.",
		unavailable: selectedPlant
			? null
			: plantsLoading
				? "Loading warehouses…"
				: plantLoadError
					? "Could not load warehouses. Please refresh and try again."
					: !plantOptions.length
						? "No warehouses are available. Create a warehouse before customizing its theme."
						: "Select a warehouse above to view or edit its theme.",
		picker: showPicker ? { options: plantOptions || [], value: selectedPlant, onChange: updatePlantId } : null,
	};
}

// Organization layer: own = the org's stored themeSettings (URL mode), parent = nothing, so every
// unset field resolves to the application default. Same cache entry as brandingContext's org read.
function useOrgThemeSource(orgId) {
	const { userDetail } = useUserDetail();
	const userId = userDetail?.userId;
	const query = useGetOrgBrandingQuery({ orgId, userId }, { refetchOnMountOrArgChange: 60, skip: !orgId });
	const [updateOrgTheme, { isLoading }] = useUpdateOrgThemeMutation();
	// `data` can belong to a previous argument; only a response for this org may be edited.
	const result = orgId ? query.currentData?.data : undefined;
	const data = result && String(result.orgId) === String(orgId) ? result : undefined;
	const assetsExpireAt = data?.assetsExpireAt;
	const refetch = query.refetch;
	useEffect(() => {
		const expiresAt = Date.parse(assetsExpireAt);
		if (!orgId || !Number.isFinite(expiresAt)) return;

		// The company editor may target a different org than BrandingProvider refreshes.
		const refreshAt = expiresAt - 60_000;
		let timer;
		let stopped = false;
		let refreshing = false;
		async function refreshIfNeeded() {
			if (stopped || refreshing || document.visibilityState === "hidden" || Date.now() < refreshAt) return;
			clearTimeout(timer);
			refreshing = true;
			try {
				await refetch();
			} catch {
				// Keep the draft intact and retry transient failures below.
			} finally {
				refreshing = false;
				// New URLs restart this effect; otherwise retry without a tight request loop.
				if (!stopped) timer = setTimeout(refreshIfNeeded, 60_000);
			}
		}

		timer = setTimeout(refreshIfNeeded, Math.max(0, refreshAt - Date.now()));
		window.addEventListener("focus", refreshIfNeeded);
		window.addEventListener("online", refreshIfNeeded);
		document.addEventListener("visibilitychange", refreshIfNeeded);
		return () => {
			stopped = true;
			clearTimeout(timer);
			window.removeEventListener("focus", refreshIfNeeded);
			window.removeEventListener("online", refreshIfNeeded);
			document.removeEventListener("visibilitychange", refreshIfNeeded);
		};
	}, [userId, orgId, assetsExpireAt, refetch]);
	// A settled response for a different org (the server resolved the caller's own org) will
	// never become editable: report it instead of loading forever.
	const wrongOrg = !!result && !data && !query.isFetching;
	const isError = !!orgId && (!!query.isError || wrongOrg);
	return {
		userId, orgId, targetId: orgId, idKey: "orgId",
		ownLayer: data?.themeSettings || EMPTY_LAYER, parentLayer: EMPTY_LAYER, revision: data?.revision ?? 0,
		isReady: !!(data && query.isSuccess && !isError), isError, isSaving: isLoading,
		save: (patch) => updateOrgTheme({ orgId, ...patch }),
		refetch: () => query.refetch(),
		title: "Organization theme",
		subtitle: () => "Applies to every warehouse that hasn't overridden a field.",
		unavailable: orgId ? null : "Select a warehouse under Warehouse to choose its organization.",
		picker: null,
	};
}

function ThemeEditorBody({ scope, source, canEdit, scopeSwitch, onDirtyChange, guardsPlantSwitch }) {
 const copy = SCOPE_COPY[scope];
 const { ownLayer: overrides, parentLayer, revision: liveRevision, isReady, isError } = source;
 const canEditTheme = !!canEdit;
 const isLoading = source.isSaving;
 const [pendingOps, setPendingOps] = useState({});
 const [editorRevision, setEditorRevision] = useState(liveRevision);
 const [needsReload, setNeedsReload] = useState(false);
 const [savePending, setSavePending] = useState(false);
 const [readingFiles, setReadingFiles] = useState({});
 const objectUrlsRef = useRef(new Set());
 const active = useRef(false);
 const saving = useRef(false);
 const fileReads = useRef({});
 const isSaving = isLoading || savePending;
 const isReading = Object.keys(readingFiles).length > 0;
 const isDirty = Object.keys(pendingOps).length > 0 || isReading;
 const isStale = needsReload || (isReady && liveRevision !== editorRevision);
 const controlsDisabled = !canEditTheme || !isReady || isError || isStale || isSaving;
 const editable = useRef(false);
 editable.current = !controlsDisabled;

 function revokeTrackedObjectUrls() {
  objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
  objectUrlsRef.current.clear();
 }
 function cancelFileRead(path) {
  delete fileReads.current[path];
  setReadingFiles(prev => { const next = { ...prev }; delete next[path]; return next; });
 }
 function cancelAllFileReads() { fileReads.current = {}; setReadingFiles({}); }
 useEffect(() => {
  active.current = true;
  return () => { active.current = false; fileReads.current = {}; revokeTrackedObjectUrls(); };
 }, []);
 useEffect(() => {
  if (isReady && !isDirty && !needsReload && !isSaving) setEditorRevision(liveRevision);
 }, [isReady, isDirty, needsReload, isSaving, liveRevision]);
 useEffect(() => {
  if (!isDirty && !isSaving) return;
  return registerLeaveGuard({
   label: "Theme",
   guardsPlantSwitch: !!guardsPlantSwitch,
   message: "Discard unsaved theme changes and leave?",
   isBusy: () => saving.current || isSaving,
   onBusy: () => toastMessage(false, "Wait for the theme save to finish before leaving."),
  });
 }, [isDirty, isSaving, guardsPlantSwitch]);
 const onDirtyChangeRef = useRef(onDirtyChange);
 onDirtyChangeRef.current = onDirtyChange;
 useEffect(() => {
  onDirtyChangeRef.current?.(isDirty);
 }, [isDirty]);
 // An unmounting editor (org switch, page leave) no longer holds unsaved work.
 useEffect(() => () => onDirtyChangeRef.current?.(false), []);

	const isLeafOverridden = (path) => leafHasOwn(overrides, path);

	function setScalar(path, value) {
        if (!editable.current || saving.current) return;
        cancelFileRead(path);
		setPendingOps((prev) => {
			const next = { ...prev };
			if (isLeafOverridden(path) && leafGet(overrides, path) === value) delete next[path];
			else next[path] = { op: "set", value };
			return next;
		});
	}
	function useOrgDefault(path) {
        if (!editable.current || saving.current) return;
        cancelFileRead(path);
		setPendingOps((prev) => {
			const next = { ...prev };
			if (isLeafOverridden(path)) next[path] = { op: "unset" };
			else delete next[path];
			return next;
		});
	}
	function hideAsset(path) {
        if (!editable.current || saving.current || !canHideInScope(scope)) return;
        cancelFileRead(path);
		setPendingOps((prev) => {
			const next = { ...prev };
			if (isLeafOverridden(path) && leafGet(overrides, path) === null) delete next[path];
			else next[path] = { op: "hide" };
			return next;
		});
	}
	// The inverse of `hideAsset`: get the draft back off `null` so the inherited image shows again.
	// A saved `null` has to be explicitly unset; a merely pending hide just needs dropping.
	function unhideAsset(path) {
        if (!editable.current || saving.current || !canHideInScope(scope)) return;
        cancelFileRead(path);
		setPendingOps((prev) => {
			const next = { ...prev };
			if (isLeafOverridden(path) && leafGet(overrides, path) === null) next[path] = { op: "unset" };
			else delete next[path];
			return next;
		});
	}
	function cancelFieldEdit(path) {
        if (!editable.current || saving.current) return;
        cancelFileRead(path);
		setPendingOps((prev) => {
			if (!(path in prev)) return prev;
			const next = { ...prev };
			delete next[path];
			return next;
		});
	}
 async function setAssetFile(path, file) {
  if (!file || !editable.current || saving.current) return;
  const token = {};
  fileReads.current[path] = token;
  setReadingFiles(prev => ({ ...prev, [path]: true }));
  try {
   const descriptor = await fileToUploadDescriptor(file);
   if (!active.current || fileReads.current[path] !== token || saving.current) return;
   const previewUrl = URL.createObjectURL(file);
   objectUrlsRef.current.add(previewUrl);
   setPendingOps(prev => ({ ...prev, [path]: { op: "set", value: descriptor, previewUrl } }));
  } catch (error) {
   if (active.current && fileReads.current[path] === token) toastMessage(false, error instanceof AssetFileError ? error.message : "Could not read the selected image.");
  } finally {
   if (active.current && fileReads.current[path] === token) cancelFileRead(path);
  }
 }
 function discardChanges() {
  if (saving.current) return;
  cancelAllFileReads(); revokeTrackedObjectUrls(); setPendingOps({});
 }
 function resetAllDraft() {
  if (!editable.current || saving.current) return;
  cancelAllFileReads(); revokeTrackedObjectUrls();
  setPendingOps(Object.fromEntries(LEAF_PATHS.filter(path => isLeafOverridden(path)).map(path => [path, { op: "unset" }])));
 }
 async function reloadLatest() {
  if (saving.current || !window.confirm("Reload the latest theme? This discards your unsaved changes.")) return;
  cancelAllFileReads();
  const result = await source.refetch();
  if (!active.current) return;
  if (result.error || String(result.data?.data?.[source.idKey]) !== String(source.targetId)) {
   toastMessage(false, copy.reloadError); return;
  }
  revokeTrackedObjectUrls(); setPendingOps({});
  setEditorRevision(result.data.data.revision ?? 0); setNeedsReload(false);
 }
 async function handleSave() {
  if (!editable.current || saving.current || !isDirty || Object.keys(fileReads.current).length > 0 || !source.targetId) return;
  // The server rejects an empty or over-long title, so settle it here: a title cleared to blank
  // means "inherit" (unset an own value, otherwise nothing to send), and a kept one is trimmed.
  const ops = { ...pendingOps };
  if (ops.title?.op === "set") {
   const title = String(ops.title.value ?? "").trim();
   if (title.length > TITLE_MAX_LENGTH) {
    toastMessage(false, `Website title must be ${TITLE_MAX_LENGTH} characters or fewer.`);
    return;
   }
   if (title) ops.title = { op: "set", value: title };
   else if (isLeafOverridden("title")) ops.title = { op: "unset" };
   else delete ops.title;
  }
  if (!Object.keys(ops).length) {
   setPendingOps({});
   return;
  }
  saving.current = true; setSavePending(true);
  const { set, unset } = buildThemePatch(ops);
  try {
   const res = await source.save({ expectedRevision: editorRevision, set, unset }).unwrap();
   if (res.error) throw { data: res };
   if (!active.current) return;
   revokeTrackedObjectUrls(); setPendingOps({}); setEditorRevision(res.data.revision);
   toastMessage(true, res.message);
  } catch (err) {
   if (!active.current) return;
   const error = err?.data || err;
   toastMessage(false, error?.message || "Could not confirm the theme save. Reload before retrying.");
   // Conflicts and ambiguous failures must be reviewed, never replayed automatically.
   if (!["INVALID_THEME_INPUT", "EMPTY_PATCH", "THEME_FORBIDDEN", "THEME_ASSET_UPLOAD_FAILED"].includes(error?.errorCode)) {
    setNeedsReload(true); source.refetch();
   }
  } finally {
   saving.current = false;
   if (active.current) setSavePending(false);
  }
 }

	const draftOverrides = applyPendingOpsToDraft(overrides, pendingOps);
	const preview = resolveBranding(draftOverrides, parentLayer);
	// Free-text/color inputs must echo exactly what the user typed while a pending "set" exists —
	// even an interim value the resolver would normalize away (e.g. a cleared title) — rather than
	// the normalized preview, or the field would visibly snap back mid-keystroke.
	function rawFieldValue(path, resolvedValue) {
		const pending = pendingOps[path];
		return pending && pending.op === "set" ? pending.value : resolvedValue;
	}

	const changedCount = Object.keys(pendingOps).length;
	const customizedCount = LEAF_PATHS.filter((path) => isLeafOverridden(path)).length;
	const picker = source.picker;
	const statusText = isSaving ? "Saving…" : isReading ? "Reading image…" : changedCount ? `${changedCount} unsaved change${changedCount === 1 ? "" : "s"}` : "No unsaved changes";

	return (
		<ThemeScopeContext.Provider value={scope}>
			<div className="theme-scope-bar">
				{scopeSwitch ? <div className="theme-scope-switch-wrap">{scopeSwitch}</div> : null}
				<div className="theme-scope-text">
					<span className="theme-scope-title">{source.title}</span>
					<span className="theme-scope-sub">{source.subtitle(customizedCount)}</span>
				</div>
				{picker ? (
					<div className="theme-scope-picker">
						<Dropdown label="Select Warehouse" options={picker.options} value={picker.value} onChange={picker.onChange} disableClearable={true} disabled={isSaving} />
					</div>
				) : null}
			</div>

			{source.unavailable ? (
				<div className="theme-message">{source.unavailable}</div>
			) : isError ? (
				<div className="theme-message">{copy.loadError}</div>
			) : !isReady ? (
				<Loader size="2rem" height="8.5rem" />
			) : (
				<div className="theme-editor-layout">
					<div className="theme-editor-fields">
						{isStale ? (
							<div className="theme-stale-banner">
								<span>{copy.stale}</span>
								<Button text="Reload latest" onClick={reloadLatest} style={{ ...buttonClasses.lynkitOrangeEmpty, width: "fit-content" }} />
							</div>
						) : null}

						<SettingsPanel title="Branding" subtitle="Name and marks shown across the app">
							<FieldRow
								label="Website Title"
								hint={`Browser tab and header · max ${TITLE_MAX_LENGTH} characters · leave empty to inherit`}
								resolved={preview.title}
								dirty={!!pendingOps["title"]}
								disabled={controlsDisabled}
								canRevert={isLeafOverridden("title") || !!pendingOps["title"]}
								onRevert={() => useOrgDefault("title")}
							>
								<Input placeholder="Enter Website Title" maxLength={TITLE_MAX_LENGTH} value={rawFieldValue("title", preview.title.value) || ""} onChange={(e) => setScalar("title", e.target.value)} disabled={controlsDisabled} />
							</FieldRow>

							<AssetRow
								label="Logo"
								hint="PNG, JPEG, WebP or ICO · max 2 MB · transparent background · about 180×40"
								path="logo"
								allowedExtensions={ASSET_ACCEPT}
								resolved={preview.logo}
								overridden={isLeafOverridden("logo")}
								hasPendingOp={!!pendingOps["logo"]}
								onUpload={(file) => setAssetFile("logo", file)}
								onCancelEdit={() => cancelFieldEdit("logo")}
								onHide={() => hideAsset("logo")}
								onUnhide={() => unhideAsset("logo")}
								onUseOrgDefault={() => useOrgDefault("logo")}
								disabled={controlsDisabled}
							/>
							<AssetRow
								label="Favicon"
								hint="PNG, ICO, JPEG or WebP · max 2 MB · square · 32×32"
								path="favIcon"
								allowedExtensions={ASSET_ACCEPT}
								resolved={preview.favIcon}
								overridden={isLeafOverridden("favIcon")}
								hasPendingOp={!!pendingOps["favIcon"]}
								onUpload={(file) => setAssetFile("favIcon", file)}
								onCancelEdit={() => cancelFieldEdit("favIcon")}
								onHide={() => hideAsset("favIcon")}
								onUnhide={() => unhideAsset("favIcon")}
								onUseOrgDefault={() => useOrgDefault("favIcon")}
								disabled={controlsDisabled}
							/>
						</SettingsPanel>

						<SettingsPanel title="Appearance" subtitle={copy.appearanceSub}>
							<FieldRow
								label="Mode"
								hint="Light or dark chrome"
								resolved={preview.theme.selectedTheme}
								dirty={!!pendingOps["theme.selectedTheme"]}
								disabled={controlsDisabled}
								canRevert={isLeafOverridden("theme.selectedTheme") || !!pendingOps["theme.selectedTheme"]}
								onRevert={() => useOrgDefault("theme.selectedTheme")}
							>
								<div className="theme-segmented" role="group" aria-label="Mode">
									{modeOptions.map((option) => {
										const active = preview.theme.selectedTheme.value === option.value;
										return (
											<button
												key={option.value}
												type="button"
												className={active ? "is-active" : ""}
												aria-pressed={active}
												disabled={controlsDisabled}
												onClick={() => setScalar("theme.selectedTheme", option.value)}
											>
												{option.label}
											</button>
										);
									})}
								</div>
							</FieldRow>

							<FieldRow
								label="Primary Color"
								hint="Buttons, links and highlights"
								resolved={preview.theme.primaryColor}
								dirty={!!pendingOps["theme.primaryColor"]}
								disabled={controlsDisabled}
								canRevert={isLeafOverridden("theme.primaryColor") || !!pendingOps["theme.primaryColor"]}
								onRevert={() => useOrgDefault("theme.primaryColor")}
							>
								<div className="theme-color">
									<ColorPicker disabled={controlsDisabled} onChange={(value) => setScalar("theme.primaryColor", value)} value={rawFieldValue("theme.primaryColor", preview.theme.primaryColor.value)} />
									<ContrastNote color={rawFieldValue("theme.primaryColor", preview.theme.primaryColor.value)} />
								</div>
							</FieldRow>
						</SettingsPanel>

						<SettingsPanel title="Dashboard tiles" subtitle="Icons shown on the dashboard, edited in place">
							<div className="theme-tiles">
								{STAT_TILES.map((tile) => (
									<StatTile
										key={tile.key}
										tile={tile}
										color={preview.theme.primaryColor.value || "#ff7200"}
										path={`statIcon.${tile.key}`}
										resolved={preview.statIcon[tile.key]}
										overridden={isLeafOverridden(`statIcon.${tile.key}`)}
										hasPendingOp={!!pendingOps[`statIcon.${tile.key}`]}
										onUpload={(file) => setAssetFile(`statIcon.${tile.key}`, file)}
										onCancelEdit={() => cancelFieldEdit(`statIcon.${tile.key}`)}
										onHide={() => hideAsset(`statIcon.${tile.key}`)}
										onUnhide={() => unhideAsset(`statIcon.${tile.key}`)}
										onUseOrgDefault={() => useOrgDefault(`statIcon.${tile.key}`)}
										disabled={controlsDisabled}
									/>
								))}
							</div>
						</SettingsPanel>
					</div>

					<div className="theme-preview-pane">
						<div className="theme-preview-sticky">
							<div className="theme-preview-title">Live preview</div>
							<ThemePreview preview={preview} />
							<p className="theme-preview-note">{copy.previewNote}</p>
						</div>
					</div>
				</div>
			)}

			{!source.unavailable && isReady ? (
				<div className={`theme-actionbar${isDirty ? " is-dirty" : ""}`}>
					<span className="theme-actionbar-status">{statusText}</span>
					<div className="theme-actionbar-buttons">
						<Button onClick={resetAllDraft} text={copy.resetAll} variant="text" style={{ width: "fit-content", color: "var(--gray-color)" }} disabled={controlsDisabled} />
						<Button onClick={discardChanges} text="Discard changes" style={{ ...buttonClasses.lynkitBlackEmpty, width: "fit-content", ...MUTED_WHEN_DISABLED }} disabled={!isDirty || isSaving} />
						<Button onClick={handleSave} text="Save" style={{ ...buttonClasses.lynkitOrangeEmpty, width: "fit-content", ...MUTED_WHEN_DISABLED }} disabled={!isDirty || isReading || controlsDisabled} />
					</div>
				</div>
			) : null}
		</ThemeScopeContext.Provider>
	);
}

function a11yProps(index) {
	return {
		id: `tab-${index}`,
		"aria-controls": `tabpanel-${index}`,
	};
}

const modeOptions = [
	{ label: "Light", value: "lightMode" },
	{ label: "Dark", value: "darkMode" },
];

const datePrefixOptions = [
	{ label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
	{ label: "DD/MM/YY", value: "DD/MM/YY" },
	{ label: "MM/YY", value: "MM/YY" },
	{ label: "MM/YYYY", value: "MM/YYYY" },
	{ label: "DD/MM", value: "DD/MM" },
	{ label: "MM/DD/YYYY", value: "MM/DD/YYYY" },
	{ label: "MM/DD/YY", value: "MM/DD/YY" },
	{ label: "MMDDYY", value: "MMDDYY" },
	{ label: "MMDDYYYY", value: "MMDDYYYY" },
	{ label: "MMYY", value: "MMYY" },
	{ label: "DDMM", value: "DDMM" },
	{ label: "MMYYYY", value: "MMYYYY" },
];

const financialPrefixOptions = [
	{ label: "YY-YY", value: "short" },
	{ label: "YYYY-YY", value: "long" },
];

const productPrefixOptions = [
	{ label: "Prefix", value: "prefix" },
	{ label: "Product id", value: "productId" },
];

const productCatPrefixOptions = [
	{ label: "Prefix", value: "prefix" },
	{ label: "Product Categories id", value: "productCategoryId" },
];

const supplierPrefixOptions = [
	{ label: "Prefix", value: "prefix" },
	{ label: "Supplier id", value: "supplierId" },
];

const prefixTypeOptions = [
	{ label: "Date", value: "date" },
	{ label: "Global", value: "global" },
	{ label: "Counter", value: "counter" },
	{ label: "Financial Year", value: "finance_year" },
	{ label: "Product", value: "product" },
	{ label: "ProductCategories", value: "productCategories" },
	{ label: "Supplier", value: "supplier" },
];

const resetOptions = [
	{ label: "Day", value: "day" },
	{ label: "Month", value: "month" },
	{ label: "Year", value: "year" },
	{ label: "Financial Year", value: "finance_year" },
];

const pdfDocTypeOptions = [{ label: "Vehicle Detail", value: "vehicle_detail" }];
